import { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { MdOutlineDashboard, MdOutlineSettings } from "react-icons/md";
import { PiBookOpenLight, PiBriefcaseLight, PiChartLineUpLight, PiChatCircleLight, PiCreditCardLight, PiExamLight, PiFileTextLight, PiFolderLight, PiGraduationCapLight, PiHandHeartLight, PiPowerBold, PiQuestionLight, PiSealCheckLight, PiStarLight, PiStudentLight, PiTrophyLight, PiUsersThreeBold, PiUsersThreeLight } from "react-icons/pi";
import { GlobalContext } from "../../context";
import { logoutHandler } from "../../context/actions/auth";
import Logo from "../AllVon/VonHeader/assets/logo.png";
import { goToCommunity } from "../../utils/community";



export default function Sidebar({ collapsed, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const { authState: { data }, authDispatch } = useContext(GlobalContext);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [joiningCommunity, setJoiningCommunity] = useState(false);

  const isInstructor = data?.user?.is_instructor;
  const isAdmin      = data?.user?.is_admin;
  const isStudent    = !isAdmin && !isInstructor;

  const adminNavItems = [
    { label: "Overview",      path: "/dashboard",                       icon: MdOutlineDashboard, exact: true },
    { label: "Students",      path: "/dashboard/admin/students",        icon: PiStudentLight },
    { label: "Cohorts",       path: "/dashboard/admin/cohorts",         icon: PiUsersThreeLight },
    { label: "Scholarships",  path: "/dashboard/admin/scholarships",    icon: PiGraduationCapLight },
    { label: "Job Board",     path: "/dashboard/admin/jobs",            icon: PiBriefcaseLight },
    { label: "Schedules",     path: "/dashboard/admin/schedules",       icon: PiSealCheckLight },
    { label: "Comments",      path: "/dashboard/admin/comments",        icon: PiChatCircleLight },
    { label: "Feedback",      path: "/dashboard/admin/feedback",        icon: PiStarLight },
    { label: "Sponsors",      path: "/dashboard/admin/sponsors",        icon: PiHandHeartLight },
    { label: "Partners",      path: "/dashboard/admin/partners",        icon: PiUsersThreeLight },
    { label: "FAQs",          path: "/dashboard/admin/faqs",            icon: PiQuestionLight },
    { label: "Leaderboard",   path: "/dashboard/admin/leaderboard",     icon: PiTrophyLight },
    { label: "Subscriptions", path: "/dashboard/admin/subscriptions",   icon: PiCreditCardLight },
    { label: "Analytics",     path: "/dashboard/admin/analytics",       icon: PiChartLineUpLight },
    { label: "Settings",      path: "/dashboard/settings",              icon: MdOutlineSettings },
  ];

  const navItems = isAdmin ? adminNavItems : isInstructor ? [
    { label: "Overview",       path: "/dashboard",                           icon: MdOutlineDashboard, exact: true },
    { label: "My Classrooms",  path: "/dashboard/instructor/courses",        icon: PiBookOpenLight },
    { label: "Submissions",    path: "/dashboard/instructor/submissions",     icon: PiSealCheckLight },
    { label: "Leaderboard",    path: "/dashboard/instructor/leaderboard",    icon: PiTrophyLight },
    { label: "Settings",       path: "/dashboard/settings",                  icon: MdOutlineSettings },
  ] : [
    // Students: grouped as Learn / Career / Account (LMS revamp)
    { group: "Learn",   label: "Home",          path: "/dashboard",                icon: MdOutlineDashboard, exact: true },
    { group: "Learn",   label: "My courses",    path: "/dashboard/my-courses",     icon: PiBookOpenLight },
    { group: "Learn",   label: "Grades",        path: "/dashboard/grades",         icon: PiExamLight },
    { group: "Learn",   label: "Projects",      path: "/dashboard/projects",       icon: PiFolderLight },
    { group: "Learn",   label: "Leaderboard",   path: "/dashboard/leaderboard",    icon: PiTrophyLight },
    { group: "Career",  label: "Certificates",  path: "/dashboard/certificates",   icon: PiSealCheckLight },
    { group: "Career",  label: "Job board",     path: "/dashboard/job-board",      icon: PiBriefcaseLight },
    { group: "Career",  label: "CV reviewer",   path: "/dashboard/cv-review",      icon: PiFileTextLight },
    { group: "Account", label: "Enroll",        path: "/dashboard/enroll",         icon: PiGraduationCapLight },
    { group: "Account", label: "Subscriptions", path: "/dashboard/subscriptions",  icon: PiCreditCardLight },
    { group: "Account", label: "Settings",      path: "/dashboard/settings",       icon: MdOutlineSettings },
  ];

  // Hands the user off to the Community platform; on success the browser leaves
  // this page, so the loading state is only cleared when something goes wrong.
  const handleJoinCommunity = async () => {
    if (joiningCommunity) return;
    setJoiningCommunity(true);
    try {
      await goToCommunity();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Couldn't open Community. Please try again.");
      setJoiningCommunity(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const executeLogout = async () => {
    setShowLogoutModal(false);
    await logoutHandler(authDispatch);
    navigate("/");
  };

  const isActive = (path, exact) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <>
      {/* ── Mobile backdrop ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* ── Sidebar panel ── */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white z-50 flex flex-col
          border-r border-gray-200 shadow-sm overflow-hidden
          w-64
          transition-[width,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:z-auto lg:shadow-none lg:flex-shrink-0
          ${collapsed ? "lg:w-16" : "lg:w-64"}
        `}
      >
        {/* ── Mobile-only header: logo + close button ──
            On desktop the logo lives in the top bar, not here. */}
        <div className="lg:hidden flex items-center justify-between px-4 h-16
          border-b border-gray-100 flex-shrink-0">
          <Link to="/" onClick={onMobileClose}>
            <img src={Logo} alt="E1X Academy" className="h-9 w-auto object-contain" />
          </Link>
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700
              hover:bg-gray-100 transition-colors"
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 px-2 py-4 flex flex-col gap-0.5 overflow-y-auto">

          {navItems.map(({ label, path, icon: Icon, exact, group }, i) => {
            const active = isActive(path, exact);
            // Section label ("Menu", or the student groups Learn / Career / Account) — collapses on desktop
            const heading = i === 0 ? (group || "Menu") : group && group !== navItems[i - 1].group ? group : null;
            return (
              <div key={label}>
              {heading && (
                <div
                  className={`
                    overflow-hidden transition-all duration-200
                    h-6 opacity-100 mb-2 ${i > 0 ? "mt-4" : ""}
                    ${collapsed ? "lg:h-0 lg:opacity-0 lg:mb-0 lg:mt-0" : ""}
                  `}
                >
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">
                    {heading}
                  </p>
                </div>
              )}
              <Link
                to={path}
                onClick={onMobileClose}
                title={collapsed ? label : undefined}
                className={`
                  flex items-center rounded-lg py-2.5 text-sm font-medium
                  transition-all duration-200
                  ${collapsed ? "lg:justify-center lg:px-2" : "gap-3 px-3"}
                  ${active
                    ? "bg-[#FEF0EB] text-[#F05A19]"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}
                `}
              >
                <Icon size={20} className="flex-shrink-0" />

                {/* Label fades + shrinks away when collapsed */}
                <span
                  className={`
                    whitespace-nowrap overflow-hidden transition-all duration-200
                    ${collapsed ? "lg:w-0 lg:opacity-0" : "opacity-100"}
                  `}
                >
                  {label}
                </span>
              </Link>
              </div>
            );
          })}
        </nav>

        {/* ── Logout pinned to bottom ── */}
        <div className="px-2 py-3 border-t border-gray-100 flex-shrink-0 flex flex-col gap-1">
          {isStudent && (
            <button
              onClick={handleJoinCommunity}
              disabled={joiningCommunity}
              title={collapsed ? "Join Community" : undefined}
              className={`
                flex items-center w-full rounded-lg py-2.5 text-sm font-semibold
                bg-[#F05A19] text-white hover:bg-[#d14d14] disabled:opacity-70 disabled:cursor-wait
                transition-all duration-200
                ${collapsed ? "lg:justify-center lg:px-2" : "gap-3 px-3"}
              `}
            >
              <PiUsersThreeBold size={20} className="flex-shrink-0" />
              <span
                className={`
                  whitespace-nowrap overflow-hidden transition-all duration-200
                  ${collapsed ? "lg:w-0 lg:opacity-0" : "opacity-100"}
                `}
              >
                {joiningCommunity ? "Opening Community…" : "Join Community"}
              </span>
            </button>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? "Logout" : undefined}
            className={`
              flex items-center w-full rounded-lg py-2.5 text-sm font-medium
              text-red-500 hover:bg-red-50 hover:text-red-600
              transition-all duration-200
              ${collapsed ? "lg:justify-center lg:px-2" : "gap-3 px-3"}
            `}
          >
            <PiPowerBold size={20} className="flex-shrink-0" />
            <span
              className={`
                whitespace-nowrap overflow-hidden transition-all duration-200
                ${collapsed ? "lg:w-0 lg:opacity-0" : "opacity-100"}
              `}
            >
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-4">
                <PiPowerBold size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Logout</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to log out of your learning dashboard?
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeLogout}
                  className="flex-1 py-3 px-4 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 shadow-sm shadow-red-200 transition-colors"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
